<script setup lang="ts">
import { AvatarWithFallback } from '@/components/ui/avatar'
import type { FeedSummaryItem } from '@/entities/feed';
import { toFeedCardModel } from '@/components/feed/feed-card.model';
import StoryPlayer from '@/components/video/StoryPlayer.vue';
import FeedSeenTag from '@/components/feed/FeedSeenTag.vue';
import FeedCardActions from '@/components/feed/FeedCardActions.vue';

const props = defineProps({
  canAct: { type: Boolean, default: true },
  responseContext: { type: Boolean, default: false },
  item: {
    type: Object as PropType<FeedSummaryItem>,
    required: true,
  },
  active: {
    type: Boolean,
    required: true,
  },
  preload: {
    type: Boolean,
    default: false,
  },
  favoriteActive: {
    type: Boolean,
    default: false,
  },
  /** Rendered in a sheet without the bottom navigation. */
  sheet: {
    type: Boolean,
    default: false,
  },
  /** The details cover the card: the video holds until they close. */
  suspended: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  finished: [];
  action: ['primary' | 'favorite' | 'more'];
}>();

const card = computed(() => toFeedCardModel(props.item));
</script>

<template>
  <article class="resume-view relative h-full w-full overflow-hidden bg-black">
    <StoryPlayer
      :videos="item.videos"
      :active="active"
      :preload="preload"
      :sheet="sheet"
      :suspended="suspended"
      @finished="emit('finished')"
    >
      <template #default="player">
        <div
          class="resume-view__scrim"
          aria-hidden="true"
        />

        <button
          class="resume-view__sound focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          type="button"
          :aria-label="player.isMuted ? 'Включить звук' : 'Выключить звук'"
          @click.stop="player.toggleMuted"
        >
          <Icon
            :name="player.isMuted ? 'tabler:volume-off' : 'tabler:volume'"
            class="size-5"
            aria-hidden="true"
          />
        </button>

        <div class="resume-view__bottom" :class="sheet && '!pb-10'">
          <div class="resume-view__info">
            <AvatarWithFallback :src="card.avatar" :name="card.owner" class="size-10 border border-white/20" />
            <p class="resume-view__eyebrow">
              {{ card.eyebrow }}
            </p>
            <FeedSeenTag v-if="item.seen" />
            <h2 class="resume-view__title">
              {{ card.title }}
            </h2>
          </div>
          <FeedCardActions
            :primary-label="canAct ? (props.responseContext ? 'Действия с откликом' : 'Пригласить') : undefined"
            :favorite-active="props.favoriteActive"
            @action="emit('action', $event)"
          />
          <div class="resume-view__chapter">
            <p class="m-0 truncate">
              {{ player.segment?.question?.trim() || 'О себе' }}
            </p>
            <time class="shrink-0 tabular-nums">{{ player.remainingLabel }}</time>
          </div>
        </div>
      </template>
    </StoryPlayer>
  </article>
</template>

<style scoped>
.resume-view__scrim {
  position: absolute;
  inset: 0;
  z-index: 20;
  pointer-events: none;
  background: linear-gradient(180deg, rgb(0 0 0 / 28%) 0%, transparent 18%, transparent 55%, rgb(0 0 0 / 60%) 100%);
}

.resume-view__sound {
  position: absolute;
  top: 4.75rem;
  right: 1rem;
  z-index: 30;
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  color: #fff;
  background: rgb(0 0 0 / 25%);
  border: 1px solid rgb(255 255 255 / 25%);
  border-radius: 999px;
  backdrop-filter: blur(4px);
}

.resume-view__info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 0;
  color: #fff;
  pointer-events: none;
}

.resume-view__eyebrow,
.resume-view__title {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resume-view__eyebrow {
  font-size: 0.75rem;
  opacity: 0.9;
}

.resume-view__title {
  font-size: 1.375rem;
  font-weight: 600;
  line-height: 1.25;
}

.resume-view__bottom {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 1.5rem calc(6.75rem + env(safe-area-inset-bottom, 0px));
}

.resume-view__chapter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #fff;
}

@media (max-width: 767px) {
  .resume-view__sound {
    display: none;
  }
}
</style>
